import { useState, useEffect, useCallback, useMemo } from 'react'
import { Table } from 'antd'
import ReactDragColumnView from '../Drag/ReactDragColumnView'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { renderTranslation, stopPropagation } from '../../Helpers/GenericHelpers'
import { AllFreeWidthColumn, ConfigurationForm } from '../../Constants/Configuration'
import {
  emptyTableHeight,
  articleRowHeight,
  defaultArticlesToShow,
  defaultSingleRowHeight,
  ArticleDefaultColumn,
} from '../../Constants/TableConfiguration'
import {
  generateTableColumnsToRender,
  handleAvailabiltyAndSizeColumnPosition,
  mapArticleImagesAndDescription,
  triggerPagination,
} from '../../Helpers/TableHelper'
import {
  setFilterResultByAvailability,
  removeColumn,
  setActiveDropdown,
  setEditSelection,
} from '../../Slices/Omnichannel/SubRuleSlice'
import { retrieveActiveListings, retrievePricelistForSubrule, retrieveRule } from '../../Helpers/DiscountHelper'
import ManigliaIcon from '../Svg/ManigliaIcon'
import { scrollElementHeightPositionHandler, scrollElementHeightPositionTouchHandler } from '../../Helpers/TableHelper'
import { populateSubRuleTableWithArticles } from '../../Services/Omnichannel/SeasonRuleService'
import { formatString } from '../../Helpers/StringHelpers'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { TableRowSelection } from 'antd/es/table/interface'
import { BaseStorePricelist } from '../../Constants/Configuration'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { getPricelistName } from 'Helpers/PricesHelper'

function DetailSubRuleTable(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const defaultColumns = useAppSelector(state => state.present.subRule.defaultColumns)
  const negateColumns = useAppSelector(state => state.present.subRule.negateColumns)
  const sorting = useAppSelector(state => state.present.subRule.sorting)
  const count = useAppSelector(state => state.present.subRule.count)
  const filterTableColumns = useAppSelector(state => state.present.subRule.filterColumns)
  const formConfigurationValue = useAppSelector(state => state.present.configuration.formConfigurationValue)
  const subRuleArticles = useAppSelector(state => state.present.subRule.subRuleArticles)
  const filterResultByAvailability = useAppSelector(state => state.present.subRule.filterResultByAvailability)
  const activeDropdown = useAppSelector(state => state.present.subRule.activeDropdown)
  const editSelection = useAppSelector(state => state.present.subRule.editSelection)
  const freeWidthColumns = useAppSelector(state => state.present.subRule.freeWidthColumns)
  const loading = useAppSelector(state => state.present.subRule.loading)
  const allFreeWidthColumns = freeWidthColumns.indexOf(AllFreeWidthColumn) !== -1
  const typeOfChannel: TypeOfChannel = useAppSelector(state => state.present.discount.typeOfChannel)
  const allPricelists = useAppSelector(state => state.present.configuration.allPricelists)
  const isCarryOver = props?.isCarryOver
  const paginationOff = props?.paginationOff
  const [columns, setColumns] = useState([])

  const currentActiveListings = useMemo(() => {
    return retrieveActiveListings(typeOfChannel, allPricelists)
  }, [typeOfChannel, allPricelists])

  const [subRuleData, pricelistsFromSubrule] = useMemo(() => {
    const data = subRuleArticles && subRuleArticles.results ? subRuleArticles.results : []
    const priceListData = retrievePricelistForSubrule(data)
    return [data, priceListData]
  }, [subRuleArticles])

  useEffect(() => {
    const visibleColumns: ArticleDefaultColumn[] = []
    for (const group in filterTableColumns) {
      const columnsToShow = filterTableColumns[group].filter(x => x.visible)

      for (const filter in columnsToShow) {
        if (!columnsToShow[filter].active) {
          continue
        }

        const activeColumns = defaultColumns.find(item => item.name === columnsToShow[filter].value)
        if (activeColumns) {
          visibleColumns.push(activeColumns)
        }
      }
    }

    handleAvailabiltyAndSizeColumnPosition(visibleColumns)

    const filteredColumn = filterTableColumns.predefinedFilters.map(x => x.value)
    const missingColums = defaultColumns.filter(x => filteredColumn.indexOf(x.name) === -1)
    const priceListColumnsToAdd = pricelistsFromSubrule
      .map(priceList => {
        const displayName = getPricelistName(priceList.channel, language)
        const listingInformation = allPricelists.find(x => x.name === displayName || x.name === priceList.channel)
        const isTypeSell = (listingInformation && listingInformation.isSell) || !listingInformation
        return {
          name: displayName,
          channelKey: priceList.channel,
          isListing: true,
          isTypeSell,
        }
      })
      .sort((a, b) => {
        const prevIndex = currentActiveListings.findIndex(item => item.name === a.channelKey)
        const nextIndex = currentActiveListings.findIndex(item => item.name === b.channelKey)
        return prevIndex - nextIndex
      })

    setColumns(visibleColumns.concat(missingColums).concat(priceListColumnsToAdd))
  }, [defaultColumns, filterTableColumns, pricelistsFromSubrule, currentActiveListings, allPricelists, language])

  const toggleAvailability = useCallback(() => {
    dispatch(setFilterResultByAvailability())
    dispatch(populateSubRuleTableWithArticles(false))
  }, [dispatch])

  const [ySize, setYSize] = useState(emptyTableHeight)

  useEffect(() => {
    setTimeout(() => {
      const tableWrapper = document.querySelector('#sub-rule-table .ant-table-body')
      const addedHeight = tableWrapper && tableWrapper.scrollWidth !== tableWrapper.clientWidth ? 25 : 0

      const desiredHeight =
        Math.max(
          subRuleData.length === 0
            ? emptyTableHeight
            : Math.min(subRuleData.length, defaultArticlesToShow) * articleRowHeight,
          subRuleData.length === 0 ? emptyTableHeight : defaultSingleRowHeight,
        ) + addedHeight

      if (desiredHeight && desiredHeight !== ySize + 100) {
        setYSize(desiredHeight)
      }
    }, 500)
  }, [subRuleData])

  const subRuleTableColumns = useMemo(() => {
    const isConfigurationFormGruccia = formConfigurationValue === ConfigurationForm.GRUCCIA
    const priceList1 = []
    const priceList2 = []
    const pricelists = {
      priceList1Enabled: true,
      priceList1Visible: true,
      priceList1: '',
      priceList2Enabled: false,
      priceList2Visible: false,
      priceList2: '',
    }

    const alignLeftColumns = []
    const alignRightColumn = []
    const newLineColumns = []
    const isPurchasePriceVisibile = false
    return generateTableColumnsToRender(
      columns,
      subRuleData,
      language,
      defaultColumns,
      freeWidthColumns,
      allFreeWidthColumns,
      props.editDisabled,
      pricelists,
      priceList1,
      priceList2,
      isPurchasePriceVisibile,
      alignLeftColumns,
      alignRightColumn,
      newLineColumns,
      negateColumns,
      filterResultByAvailability,
      toggleAvailability,
      isConfigurationFormGruccia,
      false,
      dispatch,
      null,
      isCarryOver,
    )
  }, [
    allFreeWidthColumns,
    columns,
    defaultColumns,
    dispatch,
    filterResultByAvailability,
    formConfigurationValue,
    freeWidthColumns,
    language,
    negateColumns,
    props.editDisabled,
    subRuleData,
    toggleAvailability,
  ])

  const updateTableSize = useCallback(
    sizeToSet => {
      const tableWrapper = document.querySelector('#sub-rule-table .ant-table-body')
      const addedHeight = tableWrapper && tableWrapper.scrollWidth !== tableWrapper.clientWidth ? 25 : 0
      const minHeight = subRuleData.length ? 0 : emptyTableHeight
      const sizeToUpdate = Math.max(
        minHeight,
        sizeToSet >= articleRowHeight
          ? Math.min(sizeToSet, subRuleData.length * articleRowHeight + 10) + addedHeight
          : articleRowHeight + addedHeight,
      )
      setYSize(sizeToUpdate)
    },
    [subRuleData.length],
  )

  const handleResizeHandler = useCallback(
    mouseDownEvent => {
      scrollElementHeightPositionHandler(mouseDownEvent, ySize, updateTableSize)
    },
    [ySize, updateTableSize],
  )

  const handleResizeHandlerTouch = useCallback(
    touchEvent => {
      scrollElementHeightPositionTouchHandler(touchEvent, ySize, updateTableSize)
    },
    [ySize, updateTableSize],
  )

  const paginationWithCount = useMemo(() => {
    return {
      ...sorting,
      total: count,
      showTotal: (total, range) =>
        formatString(renderTranslation(language, TranslationKeys.tablePagination), range[0], range[1], total),
    }
  }, [sorting, count, language])

  const dragProps = useMemo(() => {
    return {
      onDragEnd(fromIndex, toIndex) {
        const cols = [...columns]
        if (cols[fromIndex - 1].name === activeDropdown || (toIndex > 0 && cols[toIndex - 1].name === activeDropdown)) {
          dispatch(setActiveDropdown(''))
        }
        if (toIndex === -1) {
          dispatch(removeColumn(cols[fromIndex - 1].name))
          dispatch(populateSubRuleTableWithArticles(false))
        }
        const item = cols.splice(fromIndex - 1, 1)[0]
        cols.splice(toIndex - 1, 0, item)
        setColumns(cols)
      },
      nodeSelector:
        'th:not(.ant-table-cell-fix-left):not(.ant-table-cell-fix-right):not(.ant-table-cell.availability-column):not(.ant-table-cell.article-title):not(.ant-table-cell.size-availability-column):not(.ant-table-cell.subrule-col-locked):not(.ant-table-cell.listing-col-locked):not(.ant-table-cell.listing-title-locked)',
      lineClassName: 'dragging',
    }
  }, [activeDropdown, columns, dispatch])

  const articlesWithImagesAndDescriptions = useMemo(() => {
    const result = mapArticleImagesAndDescription(subRuleData)
    return result
  }, [subRuleData])

  useEffect(() => {
    if (paginationOff && sorting.pageSize !== 9999) {
      const pagination = { ...sorting, pageSize: 9999 }
      dispatch(triggerPagination(null, pagination, false))
    }
  }, [dispatch, paginationOff, sorting])

  return (
    //+ 100 height to keep the div with resizable-control-point element under table
    <div style={{ height: ySize + 125 }} className={props.editDisabled ? 'noedit' : ''}>
      <ReactDragColumnView {...dragProps}>
        <Table
          id='sub-rule-table'
          className='article-table custom-scroll-body'
          columns={subRuleTableColumns}
          dataSource={articlesWithImagesAndDescriptions}
          showSorterTooltip={false}
          scroll={{ x: 'max-content', y: ySize }}
          size='middle'
          tableLayout='fixed'
          pagination={paginationOff ? false : paginationWithCount}
          loading={loading}
          onRow={record => ({
            onClick: stopPropagation,
          })}
          rowSelection={
            props.editDisabled
              ? ({
                  selectedRowKeys: editSelection,
                  columnWidth: 0,
                  hideSelectAll: props.editDisabled,
                  renderCell: (checked, record, index, originNode) => null,
                } as TableRowSelection<any>)
              : ({
                  selectedRowKeys: editSelection,
                  columnWidth: 50,
                  onChange: selectedRowKeys => {
                    const currentSelection = [...editSelection]

                    for (let i = 0; i < selectedRowKeys.length; i++) {
                      if (currentSelection.indexOf(selectedRowKeys[i]) === -1) {
                        currentSelection.push(selectedRowKeys[i])
                      }
                    }

                    const keysToRemove = subRuleData.map(result => {
                      return selectedRowKeys.indexOf(result.key) === -1 ? result.key : null
                    })

                    const selectionFiltered = currentSelection.filter(element => {
                      return keysToRemove.indexOf(element) === -1
                    })

                    dispatch(setEditSelection(selectionFiltered))
                  },
                } as TableRowSelection<any>)
          }
          onChange={(pagination, filters, sorter, extra: { currentDataSource: []; action: any }) => {
            dispatch(triggerPagination(sorter, pagination, false))
          }}
        />
      </ReactDragColumnView>
      <div
        className='resizable-control-point'
        onMouseDown={handleResizeHandler}
        onTouchStart={handleResizeHandlerTouch}
      >
        <ManigliaIcon />
      </div>
    </div>
  )
}
export default DetailSubRuleTable
